/**
 * Newsletter — 3 variants (inline_centered, card, magazine)
 */
import type { NewsletterContent } from '../schemas/newsletter';
import { Body, Btn, Eyebrow, H2, SectionFrame } from './primitives';

interface NL {
  content: NewsletterContent;
}

function EmailForm({
  placeholder,
  submitLabel,
  inverted = false,
}: {
  placeholder?: string | null;
  submitLabel: string;
  inverted?: boolean;
}) {
  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      style={{
        display: 'flex',
        gap: 8,
        background: inverted ? 'rgba(255,255,255,0.1)' : 'var(--ls-surface)',
        padding: 8,
        borderRadius: 'var(--ls-radius-md)',
        border: `1px solid ${inverted ? 'rgba(255,255,255,0.25)' : 'var(--ls-border)'}`,
        maxWidth: 480,
        width: '100%',
      }}
    >
      <input
        type="email"
        placeholder={placeholder ?? 'you@studio.com'}
        style={{
          flex: 1,
          padding: '10px 14px',
          border: 'none',
          background: 'transparent',
          color: inverted ? '#fff' : 'var(--ls-text)',
          fontSize: '0.95rem',
          outline: 'none',
        }}
      />
      <Btn variant="primary">{submitLabel}</Btn>
    </form>
  );
}

export function NewsletterInlineCentered({ content }: NL) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'center', textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
        {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
        <H2>{content.headline}</H2>
        {content.subheadline && <Body>{content.subheadline}</Body>}
        <EmailForm placeholder={content.placeholder} submitLabel={content.submitLabel} />
        {content.finePrint && (
          <Body style={{ fontSize: '0.8rem', opacity: 0.7 }}>{content.finePrint}</Body>
        )}
      </div>
    </SectionFrame>
  );
}

export function NewsletterCard({ content }: NL) {
  return (
    <SectionFrame background="surface">
      <div
        className="ls-card"
        style={{
          padding: 48,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          alignItems: 'flex-start',
          maxWidth: 720,
          margin: '0 auto',
        }}
      >
        {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
        <H2>{content.headline}</H2>
        {content.subheadline && <Body>{content.subheadline}</Body>}
        <EmailForm placeholder={content.placeholder} submitLabel={content.submitLabel} />
        {content.finePrint && (
          <Body style={{ fontSize: '0.8rem', opacity: 0.7 }}>{content.finePrint}</Body>
        )}
      </div>
    </SectionFrame>
  );
}

export function NewsletterMagazine({ content }: NL) {
  return (
    <SectionFrame>
      <div className="ls-split-pair" style={{ gap: 64 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <h2
            className="ls-h2"
            style={{
              fontSize: 'clamp(1.75rem, 3vw + 0.5rem, 3rem)',
              fontFamily: 'var(--ls-font-heading)',
            }}
          >
            {content.headline}
          </h2>
          {content.subheadline && <Body>{content.subheadline}</Body>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <EmailForm placeholder={content.placeholder} submitLabel={content.submitLabel} />
          {content.finePrint && (
            <Body style={{ fontSize: '0.8rem', opacity: 0.7 }}>{content.finePrint}</Body>
          )}
        </div>
      </div>
    </SectionFrame>
  );
}

export const newsletterVariants = {
  inline_centered: NewsletterInlineCentered,
  card: NewsletterCard,
  magazine: NewsletterMagazine,
};
